<template>
  <v-alert v-if="!showFilterSummary" type="info" class="my-3" variant="tonal" density="compact">
    {{ $t(emptyMessageKey) }}
  </v-alert>

  <v-list v-else class="my-3 bg-grey-lighten-4 rounded">
    <v-list-subheader>{{ $t("common.message.activeFilters") }}</v-list-subheader>

    <v-list-item v-if="hasLocationContext">
      <template #prepend>
        <v-icon icon="mdi-map-marker" color="primary" size="small" />
      </template>
      <v-list-item-title>
        {{ $t("common.action.location") }}:
        <span class="font-weight-medium">{{ locationLabel }}</span>
      </v-list-item-title>
    </v-list-item>

    <v-list-item v-if="filterProductCondition.name">
      <template #prepend>
        <v-icon icon="mdi-magnify" color="primary" size="small" />
      </template>
      <v-list-item-title>
        {{ $t("common.filter.byName") }}:
        <span class="font-weight-medium">{{ filterProductCondition.name }}</span>
      </v-list-item-title>
    </v-list-item>

    <v-list-item v-if="filterProductCondition.productId">
      <template #prepend>
        <v-icon icon="mdi-package-variant" color="primary" size="small" />
      </template>
      <v-list-item-title>
        {{ $t("common.filter.byProduct") }}:
        <span class="font-weight-medium">{{ selectedProductName }}</span>
      </v-list-item-title>
    </v-list-item>

    <v-list-item v-if="filterProductCondition.productClassId">
      <template #prepend>
        <v-icon icon="mdi-shape-outline" color="primary" size="small" />
      </template>
      <v-list-item-title>
        {{ $t("common.action.productClasses") }}:
        <span class="font-weight-medium">{{ selectedProductClassName }}</span>
      </v-list-item-title>
    </v-list-item>

    <v-list-item v-if="hasTagsFilter">
      <template #prepend>
        <v-icon icon="mdi-tag-multiple" color="primary" size="small" />
      </template>
      <v-list-item-title>
        {{ $t("common.action.tags") }}:
        <span class="font-weight-medium">
          {{ $t("common.message.selectedCount", { count: tagFilterCount }) }}
        </span>
      </v-list-item-title>
    </v-list-item>

    <v-list-item v-if="filterProductCondition.hasInventory">
      <template #prepend>
        <v-icon icon="mdi-archive" color="primary" size="small" />
      </template>
      <v-list-item-title>{{ $t("common.label.inventory") }}</v-list-item-title>
    </v-list-item>

    <v-list-item v-if="filterProductCondition.lowStock">
      <template #prepend>
        <v-icon icon="mdi-alert" color="warning" size="small" />
      </template>
      <v-list-item-title>{{ $t("common.label.lowStock") }}</v-list-item-title>
    </v-list-item>

    <v-list-item v-if="filterProductCondition.outOfStock">
      <template #prepend>
        <v-icon icon="mdi-alert-circle" color="error" size="small" />
      </template>
      <v-list-item-title>{{ $t("common.label.outOfStock") }}</v-list-item-title>
    </v-list-item>

    <v-list-item
      v-if="
        filterProductCondition.stockMin !== undefined ||
        filterProductCondition.stockMax !== undefined
      "
    >
      <template #prepend>
        <v-icon icon="mdi-archive" color="primary" size="small" />
      </template>
      <v-list-item-title>
        {{ $t("common.label.stockRange") }}: {{ filterProductCondition.stockMin ?? 0 }} -
        {{ filterProductCondition.stockMax ?? $t("common.label.unbounded") }}
      </v-list-item-title>
    </v-list-item>

    <v-list-item v-if="filterProductCondition.active !== undefined">
      <template #prepend>
        <v-icon
          :icon="filterProductCondition.active ? 'mdi-check-circle' : 'mdi-cancel'"
          :color="filterProductCondition.active ? 'success' : 'error'"
          size="small"
        />
      </template>
      <v-list-item-title>
        {{ $t("common.label.status") }}:
        {{
          filterProductCondition.active ? $t("common.status.active") : $t("common.status.inactive")
        }}
      </v-list-item-title>
    </v-list-item>
  </v-list>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useProductsStore } from "@/catalog/stores/products";
import { useCatalogStore } from "@/catalog/stores/catalog";
import { useProductClassesStore } from "@/catalog/stores/productClasses";
import { useAccountStore } from "@/account/stores/account";
import { useLocationStore } from "@/terminals/stores/locations";
import type { ProductFilterCondition } from "@/catalog/types/products";
import i18n from "@/app/plugins/i18n";

const props = withDefaults(
  defineProps<{
    emptyMessageKey?: string;
  }>(),
  { emptyMessageKey: "common.message.noActiveFilters" },
);

const productsStore = useProductsStore();
const catalogStore = useCatalogStore();
const productClassesStore = useProductClassesStore();
const accountStore = useAccountStore();
const locationStore = useLocationStore();

const emptyMessageKey = computed(() => props.emptyMessageKey);
const filterProductCondition = computed<ProductFilterCondition>(
  () => productsStore.filterProductCondition || {},
);
const locationBasedInventoryEnabled = computed(
  () => accountStore.account?.accountSettings?.inventory?.location_based_inventory_enabled === true,
);
const effectiveLocationId = computed<string | null | undefined>(() => {
  if (!locationBasedInventoryEnabled.value) return undefined;

  const condition = filterProductCondition.value;
  if (Object.prototype.hasOwnProperty.call(condition, "locationId")) {
    if (condition.locationId === null) return null;
    if (typeof condition.locationId === "string" && condition.locationId) {
      return condition.locationId;
    }
  }

  return locationStore.selectedLocation?.id ?? null;
});
const hasLocationContext = computed(() => {
  if (!locationBasedInventoryEnabled.value) return false;
  if (effectiveLocationId.value === null) {
    return Object.prototype.hasOwnProperty.call(filterProductCondition.value, "locationId");
  }
  return Boolean(effectiveLocationId.value);
});
const locationLabel = computed(() => {
  if (!hasLocationContext.value) return "";
  if (effectiveLocationId.value === null) return i18n.global.t("common.label.global");

  const locationId = effectiveLocationId.value;
  if (!locationId) return "";
  const selectedLocation = locationStore.selectedLocation;
  if (selectedLocation?.id === locationId) {
    return selectedLocation.display_name ?? locationId;
  }

  return (
    locationStore.globalLocations?.locations?.find((location) => location.id === locationId)
      ?.display_name ?? locationId
  );
});
const hasTagsFilter = computed(() => {
  const tags = filterProductCondition.value.tags;
  return Array.isArray(tags) && tags.length > 0;
});
const tagFilterCount = computed(() => {
  const tags = filterProductCondition.value.tags;
  return Array.isArray(tags) ? tags.length : 0;
});
const hasActiveFilters = computed(() => {
  const filters = filterProductCondition.value;
  return Boolean(
    filters.name ||
    filters.productId ||
    filters.productClassId ||
    hasTagsFilter.value ||
    filters.hasInventory ||
    filters.lowStock ||
    filters.outOfStock ||
    filters.stockMin !== undefined ||
    filters.stockMax !== undefined ||
    filters.active !== undefined,
  );
});
const showFilterSummary = computed(() => hasActiveFilters.value || hasLocationContext.value);
const selectedProductName = computed(() => {
  const productId = filterProductCondition.value.productId;
  if (!productId) return "";
  return productsStore.products.find((product) => product.id === productId)?.name ?? productId;
});
const selectedProductClassName = computed(() => {
  const classId = filterProductCondition.value.productClassId;
  if (!classId) return "";
  return (
    productClassesStore.productClasses.find((productClass) => productClass.id === classId)?.name ??
    classId
  );
});

onMounted(async () => {
  if (!catalogStore.catalog || Object.keys(catalogStore.catalog).length === 0) {
    await catalogStore.fetchCatalog();
  }
});
</script>
